import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, MoreHorizontal, Plus } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import ContextMenu from '../ui/ContextMenu';
import { workspacesForScope } from '../../scope/projectWorkspaceList';
import { workspaceMenuItems } from '../workspaces/workspaceMenuItems';
import LocalCapHint from '../plans/LocalCapHint';
import './ProjectWorkspacesGroup.css';

/**
 * "API Client › Workspaces" inside the project switcher (owner, 21.10.2026),
 * shown while API Requests is open: a group header like "Personal" and one
 * "Workspaces ›" row whose submenu lists the workspaces of the active scope
 * (scope/projectWorkspaceList) — pick one to open it, "⋯" for its actions
 * (the old workspace dropdown's menu, workspaces/workspaceMenuItems), and
 * "+ New workspace…" at the bottom. It replaces the separate workspace
 * dropdown in the title bar.
 *
 * The submenu is portaled and fixed-positioned beside the row: the switcher's
 * panel clips its own overflow.
 *
 * `controls` = { workspaces, currentWorkspace, onWorkspaceChange,
 *   onOpenWorkspaceModal, …the menu handlers }; `scope`/`projects` as the
 * switcher has them; `onDone` closes the whole switcher.
 */
export default function ProjectWorkspacesGroup({ controls, scope, projects, onDone }) {
  const { t } = useTranslation();
  const rowRef = useRef(null);
  const [sub, setSub] = useState(null); // { x, y } of the submenu, or null
  const [menu, setMenu] = useState(null); // { workspace, x, y } of a row's "⋯"
  const list = workspacesForScope({ scope, projects, workspaces: controls.workspaces });
  const current = controls.currentWorkspace;
  // The row names the open workspace only when the submenu lists it: both
  // read the same filtered list, so the row never shows another project's
  // workspace above a submenu that says there is none (26.10.2026).
  const shown = current ? list.find((ws) => String(ws.id) === String(current.id)) : null;

  const openSub = () => {
    const r = rowRef.current?.getBoundingClientRect();
    if (r) setSub({ x: r.right + 4, y: r.top });
  };
  const pick = (ws) => {
    setSub(null);
    onDone();
    if (String(ws.id) !== String(current?.id)) controls.onWorkspaceChange(ws);
  };

  return (
    <>
      <div className="px-3 border-b border-gray-100">
        <h4 className="text-xxs font-medium text-gray-600 uppercase tracking-wide">{t('projectScope.apiClient')}</h4>
      </div>
      <div
        ref={rowRef}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={!!sub}
        data-testid="project-switcher-workspaces"
        className={`project-switcher-row flex items-center gap-2 px-3 py-2 cursor-pointer text-sm ${sub ? 'bg-gray-100' : ''}`}
        onMouseEnter={openSub}
        onClick={() => (sub ? setSub(null) : openSub())}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
            e.preventDefault();
            openSub();
          } else if (e.key === 'Escape' || e.key === 'ArrowLeft') {
            setSub(null);
          }
        }}
      >
        <span className="min-w-0 flex-1 truncate text-gray-900">{t('projectScope.workspaces')}</span>
        {shown && <span className="pws-current truncate">{current.name}</span>}
        <ChevronRight size={14} className="flex-shrink-0 text-gray-500" aria-hidden />
      </div>

      {sub &&
        createPortal(
          <div
            className="pws-sub bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col overflow-hidden"
            style={{ left: sub.x, top: sub.y }}
            role="menu"
            aria-label={t('projectScope.workspaces')}
            data-testid="project-workspaces-menu"
            onMouseLeave={(e) => {
              if (!rowRef.current?.contains(e.relatedTarget)) setSub(null);
            }}
          >
            <OsScroll className="flex-1 min-h-0" options={{ overflow: { x: 'hidden' } }}>
              <div className="pws-list">
                {list.length === 0 && <div className="px-3 py-4 text-center text-xs text-gray-500">{t('projectScope.noWorkspaces')}</div>}
                {list.map((ws) => {
                  const selected = String(ws.id) === String(current?.id);
                  return (
                    <div
                      key={ws.id}
                      role="menuitemradio"
                      aria-checked={selected}
                      tabIndex={0}
                      className="project-switcher-row pws-row flex items-center gap-2 px-3 py-2 cursor-pointer text-sm"
                      onClick={() => pick(ws)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') pick(ws);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate text-gray-900">{ws.name}</span>
                      {selected && <Check size={14} className="project-switcher-check flex-shrink-0" aria-hidden />}
                      <button
                        type="button"
                        className="pws-more"
                        aria-label={t('projectScope.workspaceActions', { name: ws.name })}
                        aria-haspopup="menu"
                        onClick={(e) => {
                          e.stopPropagation();
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenu({ workspace: ws, x: r.right, y: r.bottom + 4 });
                        }}
                      >
                        <MoreHorizontal size={14} aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
            </OsScroll>
            <div className="p-2 border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                className="workspace-dropdown-create-btn flex items-center space-x-2 w-full p-2 rounded-lg text-sm"
                onClick={() => {
                  setSub(null);
                  onDone();
                  controls.onOpenWorkspaceModal?.();
                }}
              >
                <Plus size={16} />
                <span>{t('projectScope.newWorkspace')}</span>
              </button>
              {/* The plan's workspace limit, shown in the old dropdown's header before. */}
              <LocalCapHint kind="workspaces" />
            </div>
          </div>,
          document.body
        )}

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          align="end"
          zIndex={80}
          keyboard
          label={t('projectScope.workspaceActions', { name: menu.workspace.name })}
          items={workspaceMenuItems(menu.workspace, controls, t)}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );
}
